import { Analytics } from '@trello/atlassian-analytics';
import { SourceType } from '@trello/atlassian-analytics/src/constants/Source';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { LinkButton } from 'app/common/components/controls/button/button';
import { PlanSelectionOverlay } from 'app/src/components/FreeTrial/PlanSelection';
import RouterLink from 'app/common/components/singlespa/router-link';
import styles from './UpgradePromptFull.less';
import { dontUpsell } from '@trello/config';
import { isDesktop } from '@trello/browser';
import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial/useFreeTrialEligibilityRules';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forNamespace } from 'app/common/lib/util/i18n';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';

const formatFreeTrialPrompt = forNamespace([
  'free trial existing',
  'upgrade prompt',
]);

/**
 * UpgradePromptFull props
 *
 * content: prompt copy, optionally rendered
 * cta: call to action of prompt, navigates user within the page
 * ctaLink: link for call to action
 * ctaOnClick: function expected to fire side-effects like a trackUe event,
 *             fired on click of cta
 * hidePlanOverlay: will use a link for CTA instead of showing the overlay
 * headline: prompt header
 * iconName: name of icon
 * isDarkTheme: controls dark theme
 * isDismissable: optonially renders close button
 * onDismiss: function expected to unmount prompt and make dismissal persistent for the user
 */

interface UpgradePromptFullProps {
  className?: string;
  content?: string;
  cta: string;
  ctaLink?: string;
  ctaOnClick?: () => void;
  overlayOnContinue?: () => void;
  hidePlanOverlay?: boolean;
  headline: string;
  iconName: IconName;
  isDarkTheme?: boolean;
  isDismissable?: boolean;
  onDismiss?: () => void;
  allowUpsell?: boolean;
  orgId?: string;
  source: SourceType;
}

export const UpgradePromptFull: React.FunctionComponent<UpgradePromptFullProps> = ({
  allowUpsell,
  className,
  content,
  cta,
  ctaLink,
  ctaOnClick,
  overlayOnContinue,
  headline,
  hidePlanOverlay,
  iconName,
  isDarkTheme,
  isDismissable,
  onDismiss,
  orgId,
  source,
}) => {
  const { isEligible, hasFreeTrialCredit } = useFreeTrialEligibilityRules(
    orgId,
  );
  const isFreeTrialUpgradePrompt =
    useFeatureFlag('btg.free-trial-existing-teams', false) && isEligible;
  const [showPlanOverlay, togglePlanOverlay] = useState(false);
  const closeOverlay = () => togglePlanOverlay(false);
  const openOverlay = () => togglePlanOverlay(true);
  const onSkip = () => {
    if (!hidePlanOverlay) {
      closeOverlay();
    }

    if (onDismiss) {
      onDismiss();
    }
  };

  const [freeTrial, setFreeTrial] = useState(false);
  useEffect(() => {
    if (hasFreeTrialCredit && freeTrial) {
      closeOverlay();
    }
  }, [hasFreeTrialCredit, freeTrial]);

  const onClickCTA = () => {
    if (!hidePlanOverlay) {
      openOverlay();
    }

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'prompt',
      actionSubjectId: 'bcUpgradePrompt',
      source,
      containers: {
        organization: {
          id: orgId,
        },
      },
      attributes: {
        feature: isFreeTrialUpgradePrompt ? 'freeTrial' : undefined,
      },
    });

    if (ctaOnClick) {
      ctaOnClick();
    }
  };

  const onContinueOverlay = () => {
    if (overlayOnContinue) {
      overlayOnContinue();
    }

    closeOverlay();
  };

  const promptClasses = classNames(
    styles.upgradePromptFull,
    isDarkTheme
      ? styles.upgradePromptFullDarkTheme
      : styles.upgradePromptFullLightTheme,
    className,
  );

  const contentBlock = content && (
    <div className={styles.upgradePromptFullContent}>{content}</div>
  );

  const closeButton = isDismissable && (
    <button
      className={styles.upgradePromptFullCloseButtonWrapper}
      onClick={onDismiss}
    >
      <Icon
        className={styles.upgradePromptFullCloseButton}
        name={IconName.Close}
        small={true}
      />
    </button>
  );

  const LinkComponent = ctaLink === '/business-class' ? 'a' : RouterLink;

  if (dontUpsell && !allowUpsell) {
    return null;
  }

  const ctaCopy = isFreeTrialUpgradePrompt ? formatFreeTrialPrompt('cta') : cta;

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.UpgradePrompt,
      }}
    >
      <div className={promptClasses}>
        <div className={styles.upgradePromptFullHeadline}>{headline}</div>
        {contentBlock}
        {hidePlanOverlay ? (
          <LinkComponent
            href={ctaLink}
            className={styles.upgradePromptFullCTA}
            onClick={onClickCTA}
            target={
              isDesktop() && ctaLink === '/business-class'
                ? '_blank'
                : undefined
            }
          >
            {ctaCopy}
          </LinkComponent>
        ) : (
          <LinkButton
            onClick={onClickCTA}
            className={styles.upgradePromptFullCTA}
          >
            {ctaCopy}
          </LinkButton>
        )}
        {!hidePlanOverlay && showPlanOverlay && (
          <PlanSelectionOverlay
            onClose={() => {
              togglePlanOverlay(false);
            }}
            onContinue={onContinueOverlay}
            onSkip={onSkip}
            ctaLink={ctaLink}
            orgId={orgId || ''}
            setFreeTrial={() => setFreeTrial(true)}
          />
        )}
        <div className={styles.upgradePromptFullIconBackground}>
          <Icon
            className={styles.upgradePromptFullIcon}
            name={iconName}
            small={true}
          />
        </div>
        <div className={styles.upgradePromptFullIconClippedBackground} />
        {closeButton}
      </div>
    </ErrorBoundary>
  );
};

export const UpgradePromptFullConnected: React.FunctionComponent<UpgradePromptFullProps> = (
  props,
) => (
  <ComponentWrapper>
    <UpgradePromptFull {...props} />
  </ComponentWrapper>
);
